import type { RouteLocationNormalized } from 'vue-router'
import { watch } from 'vue'
import { useAuthStore } from '@/modules/auth/stores/useAuthStore'
import { useProjectsStore } from '@/modules/projects/stores/useProjectsStore'
import { logger } from '@/core/utils/logger'

// Singleton promise to prevent concurrent fetches
let projectsLoadPromise: Promise<void> | null = null

/**
 * Wait for auth to be ready (token refresh completed)
 * Returns immediately if already ready, otherwise waits up to timeout
 */
async function waitForAuthReady(
  authStore: ReturnType<typeof useAuthStore>,
  timeoutMs: number = 5000
): Promise<boolean> {
  if (authStore.isAuthReady) {
    return true
  }

  return new Promise((resolve) => {
    let resolved = false

    const cleanup = () => {
      if (!resolved) {
        resolved = true
        clearTimeout(timeout)
        unwatch()
      }
    }

    const timeout = setTimeout(() => {
      logger.warn('[PermissionGuard] Auth ready timeout exceeded')
      cleanup()
      resolve(false)
    }, timeoutMs)

    const unwatch = watch(
      () => authStore.isAuthReady,
      (isReady) => {
        if (isReady) {
          cleanup()
          resolve(true)
        }
      },
      { immediate: true }
    )
  })
}

/**
 * Ensure projects are loaded and current project is set.
 * Uses mutex pattern to prevent race conditions - all concurrent calls
 * wait on the same promise.
 */
async function ensureProjectsLoaded(
  projectsStore: ReturnType<typeof useProjectsStore>
): Promise<boolean> {
  // Fast path: already loaded
  if (projectsStore.dataFetched && projectsStore.currentProject) {
    return true
  }

  // Mutex pattern: create promise immediately to prevent race conditions
  if (!projectsLoadPromise) {
    projectsLoadPromise = (async () => {
      try {
        if (!projectsStore.dataFetched) {
          await projectsStore.fetchAll()
        }
        if (!projectsStore.currentProject) {
          await projectsStore.initDefaultProject()
        }
      } catch (error) {
        logger.error('[PermissionGuard] Failed to load projects:', error)
      } finally {
        projectsLoadPromise = null
      }
    })()
  }

  await projectsLoadPromise
  return !!projectsStore.currentProject
}

/**
 * Check permission based on route type
 */
async function checkRoutePermission(
  to: RouteLocationNormalized,
  authStore: ReturnType<typeof useAuthStore>,
  projectsStore: ReturnType<typeof useProjectsStore>,
  permission: string,
): Promise<boolean> {
  // Always wait for auth to be ready first (token refresh might be in progress)
  const authReady = await waitForAuthReady(authStore)
  if (!authReady || !authStore.isAuthenticated) {
    logger.warn('[PermissionGuard] Auth not ready or not authenticated')
    return false
  }

  // Project-scoped routes
  if (to.meta.projectScoped) {
    // Ensure projects are loaded before checking permissions
    const hasProject = await ensureProjectsLoaded(projectsStore)

    if (!hasProject) {
      logger.warn('[PermissionGuard] No project available for project-scoped route:', to.path)
      return false
    }

    const projectPermissions = projectsStore.currentProjectPermissions
    return authStore.hasPermission(permission, projectPermissions)
  }

  // Global routes
  return authStore.hasPermission(permission)
}

/**
 * Permission guard
 * Checks if the user has the required permission to access a route
 * Redirects to dashboard if permission is denied
 */
export const permissionGuard = async (
  to: RouteLocationNormalized,
) => {
  // Skip permission check for public routes
  if (to.meta.public) {
    return true
  }

  // If no permission required, allow access
  if (!to.meta.permission) {
    return true
  }

  const authStore = useAuthStore()
  const projectsStore = useProjectsStore()
  const permission = to.meta.permission as string

  // Check if user has permission
  const hasPermission = await checkRoutePermission(to, authStore, projectsStore, permission)

  if (hasPermission) {
    return true
  } else {
    return {
      name: 'dashboard',
      query: {
        error: 'insufficient_permissions',
        message: 'Nemáte dostatečná oprávnění pro přístup k této stránce'
      }
    }
  }
}
